import axios from 'axios';

import { BASE_URL, XC_TOKEN, TABLES } from './apiConstants';


const knowledgeApi = {
  /**
   * Fetch all knowledge bases for the logged-in user
   */
  getAll: async () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) throw new Error('User not authenticated');
      
      const user = JSON.parse(userData);
      console.log('Fetching knowledge bases for user:', user);
      
      // Extract all possible user identifiers
      const userId = user.recId || user.Id;
      const userEmail = user.Email || user.email;
      
      if (!userId && !userEmail) {
        console.error('User object has no ID or email:', user);
        throw new Error('User identification not found');
      }
      
      // Fetch all records
      const response = await axios.get(`${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records`, {
        headers: { 'xc-token': XC_TOKEN },
        params: { 
          limit: 100
        }
      });
      
      console.log('Raw API response:', response.data);
      
      if (!response.data || !response.data.list) {
        console.error('API response missing data:', response);
        return [];
      }
      
      // Client-side filtering with more detailed debugging
      console.log('User ID for filtering:', userId);
      console.log('User email for filtering:', userEmail);
      
      const filteredResults = response.data.list.filter(record => {
        console.log('Checking record:', record);
        
        if (!record.Users) {
          console.log('Record has no Users field:', record);
          return false;
        }
        
        try {
          // Try to parse Users field if it's a JSON string
          let usersData = record.Users;
          if (typeof record.Users === 'string') {
            try {
              usersData = JSON.parse(record.Users);
            } catch (e) {
              // If it fails to parse, keep the original value
              console.log('Users field is not valid JSON:', record.Users);
            }
          }
          
          console.log('Users data type:', typeof usersData);
          
          // Handle different formats of Users field
          if (typeof usersData === 'object' && usersData !== null) {
            // If it's an object, check if it contains user ID or email
            console.log('Users as object:', usersData);
            const userIdStr = String(userId);
            
            // Check if the object has properties or if the object itself is the user ID
            return Object.values(usersData).some(value => 
              String(value) === userIdStr || String(value) === userEmail
            ) || String(usersData) === userIdStr || String(usersData) === userEmail;
          } else if (typeof usersData === 'string') {
            // If it's a string, check if it contains user ID or email
            console.log('Users as string:', usersData);
            return usersData.includes(String(userId)) || usersData.includes(userEmail);
          } else if (typeof usersData === 'number') {
            // If it's a number, compare directly with user ID
            console.log('Users as number:', usersData);
            return usersData === userId;
          }
          
          return false;
        } catch (err) {
          console.error('Error processing Users field:', err);
          return false;
        }
      });
      
      console.log('Filtered results:', filteredResults);
      return filteredResults;
    } catch (error) {
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Get a single knowledge base by ID
   */
  get: async (id) => {
    try {
      console.log('Fetching knowledge base with ID:', id);
      
      // Get the specific record by ID
      const response = await axios.get(
        `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records/${id}`,
        {
          headers: { 'xc-token': XC_TOKEN }
        }
      );
      
      console.log('Knowledge base data:', response.data);
      
      // Parse Files field if it's a string
      if (response.data && response.data.Files && typeof response.data.Files === 'string') {
        try {
          response.data.Files = JSON.parse(response.data.Files);
        } catch (e) {
          console.error('Error parsing Files JSON:', e);
          response.data.Files = [];
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching knowledge base:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Create a new knowledge base for the logged-in user
   */
  create: async (kbData) => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) throw new Error('User not authenticated');
      
      const user = JSON.parse(userData);
      
      // Use either the recId or Id property, whichever exists
      const userId = user.recId || user.Id;
      const userEmail = user.Email || user.email;
      
      if (!userId && !userEmail) {
        console.error('User object has no ID or email:', user);
        throw new Error('User identification not found');
      }

      // Convert files to base64 for NocoDB attachment
      const files = await Promise.all(
        kbData.files.map(async (file) => ({
          path: file.name,
          title: file.name,
          mimetype: file.type,
          size: file.size.toString(),
          data: await toBase64(file)
        }))
      );

      // Convert size to integer (bytes) instead of decimal (MB)
      const sizeInBytes = Math.round(kbData.size * 1024 * 1024);
      
      // Prepare the payload
      const payload = {
        Name: kbData.name,
        Files: JSON.stringify(files), // Ensure this is a string
        LastUpdated: new Date().toISOString(),
        Size: sizeInBytes, // Integer value in bytes
        Status: 'Processing',
        Type: kbData.type,
        Users: userId // Store just the userId as a number
      };

      console.log('Payload being sent:', payload);

      // Send the request to NocoDB
      const response = await axios.post(
        `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records`,
        payload,
        {
          headers: {
            'xc-token': XC_TOKEN,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Created knowledge base:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating knowledge base:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
 * Update an existing knowledge base
 */
update: async (id, kbData) => {
  try {
    console.log('Updating knowledge base with ID:', id, 'Data:', kbData);
    
    // Process any new file objects (File objects from input)
    const newFiles = kbData.Files.filter(file => file instanceof File);
    
    // Process existing files (objects with title/path properties)
    const existingFiles = kbData.Files.filter(file => !(file instanceof File));
    
    // Convert new files to base64 for NocoDB attachment
    const processedNewFiles = await Promise.all(
      newFiles.map(async (file) => ({
        path: file.name,
        title: file.name,
        mimetype: file.type,
        size: file.size.toString(),
        data: await toBase64(file)
      }))
    );
    
    // Combine existing and new files
    const allFiles = [...existingFiles, ...processedNewFiles];
    
    // Calculate total size
    const totalSize = allFiles.reduce((acc, file) => {
      // For File objects
      if (file instanceof File) {
        return acc + file.size;
      }
      // For processed file objects
      else if (file.size) {
        return acc + (parseInt(file.size) || 0);
      }
      return acc;
    }, 0);
    
    // Prepare the payload
    const payload = {
      Id: id, // Include the ID in the payload
      Name: kbData.Name,
      Files: JSON.stringify(allFiles), // Convert files array to string
      LastUpdated: new Date().toISOString(),
      Size: totalSize,
      Status: 'Processing' // Reset status since files were updated
    };
    
    console.log('Update payload:', payload);
    
    // Send the request to NocoDB
    const response = await axios.patch(
      `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records`,
      payload,
      {
        headers: {
          'xc-token': XC_TOKEN,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Updated knowledge base:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating knowledge base:', error.response?.data || error.message);
    throw error;
  }
},

  /**
   * Delete a knowledge base
   */
  delete: async (id) => {
    try {
      console.log('Attempting to delete knowledge base with ID:', id);
      
      // First, get the record to ensure we have the correct format
      const getResponse = await axios.get(
        `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records/${id}`,
        {
          headers: { 'xc-token': XC_TOKEN }
        }
      );
      
      console.log('Found record to delete:', getResponse.data);
      
      // For NocoDB, we need to use a specific format for deleting records
      const response = await axios({
        method: 'DELETE',
        url: `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records`,
        headers: { 
          'xc-token': XC_TOKEN,
          'Content-Type': 'application/json'
        },
        data: {
          Id: id  // This is what the error message indicated was required
        }
      });
      
      console.log('Delete response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting knowledge base:', error.response?.data || error.message);
      
      // Try with a modified approach - some NocoDB versions have different APIs
      try {
        console.log('Trying modified delete approach...');
        
        // Try to delete with proper formatting for older versions of NocoDB
        const modResponse = await axios({
          method: 'POST',  // Some APIs use POST with a _method parameter
          url: `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records`,
          headers: { 
            'xc-token': XC_TOKEN,
            'Content-Type': 'application/json'
          },
          data: {
            _method: 'delete',  // This tells the server it's actually a DELETE
            ids: [id]           // Array of IDs to delete
          }
        });
        
        console.log('Modified delete successful:', modResponse.data);
        return modResponse.data;
      } catch (secondError) {
        console.error('Modified delete also failed:', secondError.response?.data || secondError.message);
        
        // One final approach - try a direct DELETE with the ID as part of the URL
        try {
          console.log('Trying direct record delete approach...');
          
          // Some NocoDB versions expect the ID in the URL path
          const directResponse = await axios.delete(
            `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/record/${id}`, // Note: 'record' (singular) vs 'records'
            {
              headers: { 'xc-token': XC_TOKEN }
            }
          );
          
          console.log('Direct delete successful:', directResponse.data);
          return directResponse.data;
        } catch (thirdError) {
          console.error('All delete attempts failed:', thirdError.response?.data || thirdError.message);
          throw error; // Throw the original error
        }
      }
    }
  }
};

/**
 * Helper function to convert a file to base64
 */
const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

export default knowledgeApi;